import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button } from '@kanos/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { SessionExpiry } from './session-expiry.js';
import { currentPath, meQuery, startSignIn } from './session.js';
/** A whole page with one message - while signing in, or when something keeps the app away. */
export function PageNotice({ title, children }) {
    return (_jsx("main", { className: "grid min-h-dvh place-items-center px-4", children: _jsxs("section", { className: "glass grid w-full max-w-md gap-4 rounded-lg p-6 text-center sm:p-8", children: [_jsx("p", { className: "text-xl font-bold tracking-[-0.03em]", children: "kanos" }), _jsx("h1", { className: "text-2xl font-semibold tracking-[-0.02em]", children: title }), children] }) }));
}
/**
 * Renders the app only for a signed-in person: sends everyone else to the identity provider
 * (through the BFF) and says what went wrong when signing in fails or the API is away. What the
 * person may see is checked by the API; this gate is about guiding, not about protection.
 * Before the session ends it warns; once it has ended, the app leaves the screen.
 */
export function SignInGate({ children }) {
    const queryClient = useQueryClient();
    const { data: me, isPending, isError, refetch, isRefetching } = useQuery(meQuery);
    const loginFailed = new URLSearchParams(window.location.search).get('auth') === 'failed';
    const [ended, setEnded] = useState(false);
    const end = useCallback(() => {
        setEnded(true);
        // What the app had loaded leaves the memory with the screen.
        queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== meQuery.queryKey[0] });
    }, [queryClient]);
    useEffect(() => {
        if (me === null && !loginFailed && !ended)
            startSignIn(currentPath());
    }, [me, loginFailed, ended]);
    if (ended) {
        return (_jsxs(PageNotice, { title: "Sie wurden abgemeldet", children: [_jsx("p", { className: "text-muted", children: "Zum Schutz der Daten Ihrer Mandanten endet eine Anmeldung nach einer Weile ohne Aktivit\u00E4t und nach einer festen H\u00F6chstdauer. Was nicht gespeichert war, ist verloren." }), _jsx(Button, { size: "lg", onPress: () => startSignIn(currentPath()), children: "Erneut anmelden" })] }));
    }
    if (isPending || (me === null && !loginFailed)) {
        return _jsx(PageNotice, { title: "Einen Moment \u2026" });
    }
    if (isError) {
        return (_jsxs(PageNotice, { title: "Kanos ist gerade nicht erreichbar", children: [_jsx("p", { className: "text-muted", children: "Bitte versuchen Sie es in einem Moment erneut." }), _jsx(Button, { size: "lg", isPending: isRefetching, onPress: () => {
                        void refetch();
                    }, children: "Erneut versuchen" })] }));
    }
    if (me === null) {
        return (_jsxs(PageNotice, { title: "Die Anmeldung hat nicht geklappt", children: [_jsx("p", { className: "text-muted", children: "Vielleicht hat sie zu lange gedauert oder wurde abgebrochen. Melden Sie sich bitte erneut an." }), _jsx(Button, { size: "lg", onPress: () => {
                        startSignIn(currentPath());
                    }, children: "Erneut anmelden" })] }));
    }
    return (_jsxs(_Fragment, { children: [children(me), _jsx(SessionExpiry, { onEnded: end })] }));
}
//# sourceMappingURL=sign-in-gate.js.map